import React from "react";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import { ConditionsHub, ConditionDetail } from "./pages/Conditions";
import { ArticlesHub, ArticleDetail } from "./pages/Articles";
import { ClinicalInsightsHub, ClinicalInsightDetail } from "./pages/ClinicalInsights";
import { JourneysHub, JourneyDetail } from "./pages/Journeys";
import { ResearchLibrary } from "./pages/Research";
import { About, DoctorsPortal } from "./pages/About";
import AskAQuestion from "./pages/AskAQuestion";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import MedicalDisclaimer from "./pages/legal/MedicalDisclaimer";
import ScrollToTop from "./components/ScrollToTop";
import HighRiskPregnancy from "./pages/HighRiskPregnancy";
import RecurrentPregnancyLoss from "./pages/RecurrentPregnancyLoss";
import RhNegativePregnancy from "./pages/RhNegativePregnancy";
import HighBileAcidsIHCP from "./pages/HighBileAcidsIHCP";
import PregnancyWithDiabetes from "./pages/PregnancyWithDiabetes";
import IsolatedOligohydramniosPregnancy from "./pages/IsolatedOligohydramniosPregnancy";
import SLEInPregnancy from "./pages/SLEInPregnancy";
import CoeliacDiseaseInPregnancy from "./pages/CoeliacDiseaseInPregnancy";
import MultipleLargeUterineFibroidsPregnancy from "./pages/MultipleLargeUterineFibroidsPregnancy";
import PreviousDVTPregnancy from "./pages/PreviousDVTPregnancy";
import HighBloodPressurePreeclampsiaPregnancy from "./pages/HighBloodPressurePreeclampsiaPregnancy";
import { Contact } from "./pages/Contact";
import NotFound from "./pages/NotFound";
import AdminLogin from "./admin/Login";
import { ProtectedAdmin } from "./admin/AdminLayout";
import AdminDashboard from "./admin/Dashboard";
import AdminUsers from "./admin/Users";
import AdminSessions from "./admin/Sessions";
import AdminAudit from "./admin/Audit";
import AdminFlags from "./admin/Flags";
import ContentList from "./admin/content/ContentList";
import ContentEditor from "./admin/content/ContentEditor";
import SuggestionsInbox from "./admin/content/SuggestionsInbox";
import HomepageEditor from "./admin/site/HomepageEditor";
import NavigationManager from "./admin/site/NavigationManager";
import RedirectsManager from "./admin/site/RedirectsManager";
import EnquiryInbox from "./admin/site/EnquiryInbox";
import AdminSettings from "./admin/site/AdminSettings";
import SeoDashboard from "./admin/site/SeoDashboard";
import GraphExplorer from "./admin/site/GraphExplorer";
import DerivativeQueue from "./admin/site/DerivativeQueue";
import DoctorAccounts from "./admin/site/DoctorAccounts";
import InsightsPending from "./admin/content/InsightsPending";
import InsightsPrivate from "./admin/content/InsightsPrivate";
import DoctorLogin from "./doctor/DoctorLogin";
import DoctorRegister from "./doctor/DoctorRegister";
import DoctorPortal from "./doctor/DoctorPortal";

function PublicShell({ children }) { return <Layout>{children}</Layout>; }

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Admin — bare, no public layout */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedAdmin />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="sessions" element={<AdminSessions />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="feature-flags" element={<AdminFlags />} />
          <Route path="content/:type" element={<ContentList />} />
          <Route path="content/:type/:id" element={<ContentEditor />} />
          <Route path="insights-pending" element={<InsightsPending />} />
          <Route path="insights-private" element={<InsightsPrivate />} />
          <Route path="suggestions" element={<SuggestionsInbox />} />
          <Route path="homepage" element={<HomepageEditor />} />
          <Route path="navigation" element={<NavigationManager />} />
          <Route path="redirects" element={<RedirectsManager />} />
          <Route path="enquiries" element={<EnquiryInbox />} />
          <Route path="seo" element={<SeoDashboard />} />
          <Route path="graph-explorer" element={<GraphExplorer />} />
          <Route path="derivative-queue" element={<DerivativeQueue />} />
          <Route path="doctor-accounts" element={<DoctorAccounts />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Public */}
        <Route path="/*" element={
          <PublicShell>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/conditions" element={<ConditionsHub />} />
              <Route path="/conditions/:slug" element={<ConditionDetail />} />
              <Route path="/articles" element={<ArticlesHub />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />
              <Route path="/clinical-insights" element={<ClinicalInsightsHub />} />
              <Route path="/clinical-insights/:slug" element={<ClinicalInsightDetail />} />
              <Route path="/journeys" element={<JourneysHub />} />
              <Route path="/journeys/:slug" element={<JourneyDetail />} />
              <Route path="/research" element={<ResearchLibrary />} />
              <Route path="/about" element={<About />} />
              <Route path="/high-risk-pregnancy" element={<HighRiskPregnancy />} />
              <Route path="/recurrent-pregnancy-loss" element={<RecurrentPregnancyLoss />} />
              <Route path="/rh-negative-pregnancy-high-ict-titres" element={<RhNegativePregnancy />} />
              <Route path="/high-bile-acids-pregnancy-ihcp" element={<HighBileAcidsIHCP />} />
              <Route path="/pregnancy-with-diabetes" element={<PregnancyWithDiabetes />} />
              <Route path="/isolated-oligohydramnios-pregnancy" element={<IsolatedOligohydramniosPregnancy />} />
              <Route path="/sle-in-pregnancy" element={<SLEInPregnancy />} />
              <Route path="/coeliac-disease-in-pregnancy" element={<CoeliacDiseaseInPregnancy />} />
              <Route path="/multiple-large-uterine-fibroids-pregnancy" element={<MultipleLargeUterineFibroidsPregnancy />} />
              <Route path="/previous-dvt-pregnancy" element={<PreviousDVTPregnancy />} />
              <Route path="/high-blood-pressure-preeclampsia-pregnancy" element={<HighBloodPressurePreeclampsiaPregnancy />} />
              <Route path="/ask-a-question" element={<AskAQuestion />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/medical-disclaimer" element={<MedicalDisclaimer />} />
              {/* /doctors and all sub-routes are hidden from public nav and
                  redirected to /ask-a-question until the doctor portal is
                  relaunched separately. DoctorsPortal export retained for
                  future reactivation. */}
              <Route path="/doctors" element={<Navigate to="/ask-a-question" replace />} />
              <Route path="/doctors/*" element={<Navigate to="/ask-a-question" replace />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </PublicShell>
        } />
      </Routes>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
